import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Eye, CalendarClock, Building2 } from "lucide-react";

export function useAcquisitionStatus(personId) {
  const [st, setSt] = useState(null);
  useEffect(() => { if (personId) api.get(`/acquisition/persons/${personId}`).then((r) => setSt(r.data)).catch(() => setSt(null)); }, [personId]);
  return [st, setSt];
}

export function AcquisitionMenuItems({ personId, status, onDates, onOpportunity }) {
  const { startViewAs } = useAuth();
  const nav = useNavigate();
  if (!status?.applicable) return null;
  const preview = async () => {
    try {
      sessionStorage.setItem("tr-portal-open", JSON.stringify({ tab: "journey", sub: "acquisition" }));
      await startViewAs({ role: "candidate", district_org_id: null, person_id: personId });
      nav("/");
    } catch (e) { sessionStorage.removeItem("tr-portal-open"); toast.error(apiError(e)); }
  };
  return (
    <>
      <DropdownMenuItem onClick={preview} data-testid="preview-acquisition-journey"><Eye className="w-4 h-4 mr-2" /> Preview Acquisition Journey</DropdownMenuItem>
      <DropdownMenuItem onClick={onDates} data-testid="acquisition-dates"><CalendarClock className="w-4 h-4 mr-2" /> Acquisition Transaction Dates</DropdownMenuItem>
      <DropdownMenuItem onClick={onOpportunity} data-testid="acquisition-opportunity"><Building2 className="w-4 h-4 mr-2" /> Acquisition Opportunity</DropdownMenuItem>
    </>
  );
}

const OPP_FIELDS = [["agency_name", "Agency name"], ["city", "City"], ["state", "State (2 letters)"], ["adjusted_premium", "Adjusted Premium ($)", "number"],
  ["policies_in_force", "Policies in force", "number"], ["staff_count", "Number of agency staff", "number"], ["lines_of_business", "Main lines of business"]];

export function AcquisitionOpportunityDialog({ personId, status, onClose, onSaved }) {
  const [v, setV] = useState({ ...(status?.opportunity || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/acquisition/persons/${personId}/opportunity`, v); toast.success("Acquisition opportunity saved"); onSaved(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="acquisition-opportunity-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Acquisition Opportunity</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">The matched agency's key facts. The candidate sees this card once the Target Agency Identified requirement is complete; only filled-in facts are shown.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {OPP_FIELDS.map(([k, l, t]) => (
            <div key={k} className={k === "agency_name" || k === "lines_of_business" ? "sm:col-span-2" : ""}><Label className="text-xs text-slate-600">{l}</Label>
              <Input type="text" inputMode={t ? "decimal" : undefined} value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="mt-1" data-testid={`acq-opp-input-${k}`} /></div>
          ))}
          <div className="sm:col-span-2"><Label className="text-xs text-slate-600">Short summary</Label>
            <Textarea rows={3} value={v.summary || ""} onChange={(e) => setV({ ...v, summary: e.target.value })} className="mt-1" data-testid="acq-opp-input-summary" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-brand text-white" data-testid="acq-opp-save">{busy ? "Saving…" : "Save Opportunity"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AcquisitionDatesDialog({ personId, status, onClose, onSaved }) {
  const [v, setV] = useState(Object.fromEntries((status?.dates || []).map((d) => [d.key, d.date || ""])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/acquisition/persons/${personId}/dates`, v); toast.success("Acquisition dates saved"); onSaved(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="acquisition-dates-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Acquisition Transaction Dates</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">Actual dates for this candidate's transaction. They appear on the candidate's Acquisition Journey; leave blank if not yet known.</p>
        <div className="space-y-3">
          {(status?.dates || []).map((d) => (
            <div key={d.key}><Label className="text-xs text-slate-600">{d.label}</Label>
              <Input type="date" value={v[d.key] || ""} onChange={(e) => setV({ ...v, [d.key]: e.target.value })} className="mt-1" data-testid={`acq-date-${d.key}`} /></div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-brand text-white" data-testid="acq-dates-save">{busy ? "Saving…" : "Save Dates"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
